import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import axios from '../../../services/axios';
import Modal from '../../../components/Modal/Modal';
import EmployeeNameSearch from '../../../components/EmployeeNameSearch/';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';

// =============================================================================
// Estado inicial do formulário
// =============================================================================

const EMPTY_FORM_STATE = {
   voucher_type:               'VALE_ALIMENTACAO',
   reference_month:            '',
   company_paid_amount:        '',
   discount_calculation_type:  'PERCENTUAL_VALOR_PAGO',
   discount_calculation_value: '',
   voucher_status:             'PENDENTE',
   notes:                      '',
};

// =============================================================================
// Componente
// =============================================================================

/**
 * Modal para criação de benefício VA/VT desacoplado do employeeId.
 * O funcionário é selecionado via autocomplete dentro do próprio modal.
 *
 * Props:
 *   isOpen    {boolean}  — controla visibilidade
 *   onClose   {fn}       — chamado ao fechar
 *   onSuccess {fn}       — chamado após criação bem-sucedida (para refetch da listagem)
 */
export default function NewBenefitVoucherModal({ isOpen, onClose, onSuccess }) {
   const [selectedEmployee, setSelectedEmployee]       = useState(null);
   const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
   const [formData, setFormData]                       = useState(EMPTY_FORM_STATE);
   const [isSubmitting, setIsSubmitting]               = useState(false);

   // ─── Fechar e limpar estado ────────────────────────────────────
   const handleClose = useCallback(() => {
      setSelectedEmployee(null);
      setEmployeeSearchQuery('');
      setFormData(EMPTY_FORM_STATE);
      onClose();
   }, [onClose]);

   // ─── Handlers de campo ────────────────────────────────────────
   const handleFieldChange = (event) => {
      const { name, value } = event.target;
      setFormData((previous) => ({ ...previous, [name]: value }));
   };

   // ─── Submit ───────────────────────────────────────────────────
   const handleSubmit = async () => {
      if (!selectedEmployee) {
         toast.error('Selecione um funcionário.');
         return;
      }
      if (!formData.reference_month) {
         toast.error('Mês de referência é obrigatório.');
         return;
      }
      if (!formData.company_paid_amount || Number(formData.company_paid_amount) <= 0) {
         toast.error('Valor pago pela empresa deve ser maior que zero.');
         return;
      }
      if (!formData.discount_calculation_value && formData.discount_calculation_value !== 0) {
         toast.error('Valor de cálculo do desconto é obrigatório.');
         return;
      }

      setIsSubmitting(true);
      try {
         await axios.post(
            `/hr/employees/${selectedEmployee.id}/benefit-vouchers`,
            {
               voucher_type:               formData.voucher_type,
               reference_month:            formData.reference_month,
               company_paid_amount:        Number(formData.company_paid_amount),
               discount_calculation_type:  formData.discount_calculation_type,
               discount_calculation_value: Number(formData.discount_calculation_value),
               voucher_status:             formData.voucher_status,
               notes:                      formData.notes || null,
            },
         );

         toast.success(`Benefício registrado para ${selectedEmployee.full_name}!`);
         handleClose();
         onSuccess?.();
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao registrar benefício.';
         toast.error(errorMessage);
      } finally {
         setIsSubmitting(false);
      }
   };

   return (
      <Modal
         isOpen={isOpen}
         title="Registrar benefício VA / VT"
         onClose={handleClose}
         maxWidth="580px"
      >
         <FormGrid $cols={12} $colsMobile={12}>

            {/* ─── Seleção de funcionário ──────────────────────── */}
            <Field $col={12} $colMobile={12}>
               <label>Funcionário *</label>
               <EmployeeNameSearch
                  value={employeeSearchQuery}
                  onChange={setEmployeeSearchQuery}
                  onSelect={(employee) => {
                     setSelectedEmployee(employee);
                     setEmployeeSearchQuery(employee?.full_name ?? '');
                  }}
                  required
                  placeholder="Digite o nome do funcionário..."
                  showHint
               />
               {selectedEmployee && (
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                     {selectedEmployee.jobPosition?.position_name ?? ''}
                     {selectedEmployee.department?.department_name
                        ? ` · ${selectedEmployee.department.department_name}`
                        : ''}
                  </p>
               )}
            </Field>

            {/* ─── Campos do benefício ─────────────────────────── */}
            <Field $col={6} $colMobile={12}>
               <label htmlFor="bv-voucher-type">Tipo de benefício *</label>
               <select
                  id="bv-voucher-type"
                  name="voucher_type"
                  value={formData.voucher_type}
                  onChange={handleFieldChange}
               >
                  <option value="VALE_ALIMENTACAO">Vale Alimentação</option>
                  <option value="VALE_TRANSPORTE">Vale Transporte</option>
               </select>
            </Field>

            <Field $col={6} $colMobile={12}>
               <label htmlFor="bv-reference-month">Mês de referência *</label>
               <input
                  id="bv-reference-month"
                  type="month"
                  name="reference_month"
                  value={formData.reference_month}
                  onChange={handleFieldChange}
               />
            </Field>

            <Field $col={6} $colMobile={12}>
               <label htmlFor="bv-company-paid-amount">Valor pago pela empresa (R$) *</label>
               <input
                  id="bv-company-paid-amount"
                  type="number"
                  name="company_paid_amount"
                  value={formData.company_paid_amount}
                  onChange={handleFieldChange}
                  placeholder="0,00"
                  step="0.01"
                  min="0"
               />
            </Field>

            <Field $col={6} $colMobile={12}>
               <label htmlFor="bv-voucher-status">Status *</label>
               <select
                  id="bv-voucher-status"
                  name="voucher_status"
                  value={formData.voucher_status}
                  onChange={handleFieldChange}
               >
                  <option value="PENDENTE">Pendente</option>
                  <option value="PAGO">Pago</option>
               </select>
            </Field>

            <Field $col={6} $colMobile={12}>
               <label htmlFor="bv-discount-type">Tipo de cálculo do desconto *</label>
               <select
                  id="bv-discount-type"
                  name="discount_calculation_type"
                  value={formData.discount_calculation_type}
                  onChange={handleFieldChange}
               >
                  <option value="PERCENTUAL_VALOR_PAGO">% do valor pago</option>
                  <option value="PERCENTUAL_SALARIO_BASE">% do salário base</option>
                  <option value="VALOR_FIXO">Valor fixo (R$)</option>
               </select>
            </Field>

            <Field $col={6} $colMobile={12}>
               <label htmlFor="bv-discount-value">
                  {formData.discount_calculation_type === 'VALOR_FIXO'
                     ? 'Valor fixo a descontar (R$) *'
                     : 'Percentual a descontar (%) *'}
               </label>
               <input
                  id="bv-discount-value"
                  type="number"
                  name="discount_calculation_value"
                  value={formData.discount_calculation_value}
                  onChange={handleFieldChange}
                  placeholder="0,00"
                  step="0.01"
                  min="0"
               />
            </Field>

            <Field $col={12} $colMobile={12}>
               <label htmlFor="bv-notes">Observações</label>
               <input
                  id="bv-notes"
                  type="text"
                  name="notes"
                  value={formData.notes}
                  onChange={handleFieldChange}
                  placeholder="Observações opcionais..."
               />
            </Field>

            {/* ─── Ações ───────────────────────────────────────── */}
            <Field $col={12} $colMobile={12}>
               <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '4px' }}>
                  <button type="button" onClick={handleClose} disabled={isSubmitting}>
                     Cancelar
                  </button>
                  <button type="button" onClick={handleSubmit} disabled={isSubmitting}>
                     {isSubmitting ? 'Registrando...' : 'Registrar benefício'}
                  </button>
               </div>
            </Field>

         </FormGrid>
      </Modal>
   );
}

NewBenefitVoucherModal.propTypes = {
   isOpen:    PropTypes.bool.isRequired,
   onClose:   PropTypes.func.isRequired,
   onSuccess: PropTypes.func,
};
